/**
 * Detailed Inventory Tab
 * 
 * Handles granular inventory management with cost tracking, waste logging, and stock counts
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, Trash2, Edit, RefreshCw, Search,
  AlertTriangle, ChevronDown, ChevronUp, Loader2,
  TrendingDown, BarChart3, ArrowDown, ArrowUp, Check,
  X, FileText, Warehouse
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const WASTE_REASONS = [
  { id: 'expired', name: 'Expired' },
  { id: 'damaged', name: 'Damaged' },
  { id: 'spoiled', name: 'Spoiled' },
  { id: 'overproduction', name: 'Overproduction' },
  { id: 'customer_return', name: 'Customer Return' },
];

const ADJUSTMENT_REASONS = [
  { id: 'purchase', name: 'Purchase/Restock' },
  { id: 'correction', name: 'Correction' },
  { id: 'transfer', name: 'Transfer' },
  { id: 'theft', name: 'Theft/Loss' },
  { id: 'count', name: 'Stock Count Adjustment' },
];

export default function DetailedInventoryTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [units, setUnits] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [wasteLog, setWasteLog] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showWasteDialog, setShowWasteDialog] = useState(false);
  const [showCountDialog, setShowCountDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');
  
  // Item form state
  const [itemForm, setItemForm] = useState({
    name: '',
    sku: '',
    category: 'raw_ingredients',
    subcategory: '',
    description: '',
    unit_of_measure: 'units',
    quantity_on_hand: 0,
    reorder_point: 10,
    reorder_quantity: 50,
    unit_cost: 0,
    storage_location: '',
    expiry_tracking: false,
    notes: ''
  });
  
  // Adjustment form state
  const [adjustForm, setAdjustForm] = useState({
    quantity: 0,
    type: 'add',
    reason: 'purchase',
    notes: '',
    unit_cost: ''
  });
  
  // Waste form state
  const [wasteForm, setWasteForm] = useState({
    quantity: 0,
    reason: 'expired',
    notes: ''
  });
  
  // Stock count state
  const [stockCounts, setStockCounts] = useState([]);
  
  // Fetch items
  const fetchItems = useCallback(async () => {
    try {
      let params = 'limit=200';
      if (selectedCategory !== 'all') {
        params += `&category=${selectedCategory}`;
      }
      if (searchQuery) {
        params += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await api.get(`/admin/finance/inventory/items?${params}`);
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  }, [selectedCategory, searchQuery]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/inventory/categories');
      setCategories(res.data.categories || {});
      setUnits(res.data.units_of_measure || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);
  
  // Fetch low stock items
  const fetchLowStock = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/inventory/low-stock');
      setLowStockItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch low stock:', err);
    }
  }, []);
  
  // Fetch waste log
  const fetchWasteLog = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/inventory/waste?limit=50');
      setWasteLog(res.data.entries || []);
    } catch (err) {
      console.error('Failed to fetch waste log:', err);
    }
  }, []);
  
  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/inventory/analytics/summary?period=month');
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchItems();
    fetchLowStock();
    fetchWasteLog();
    fetchAnalytics();
  }, [fetchCategories, fetchItems, fetchLowStock, fetchWasteLog, fetchAnalytics]);
  
  // Refresh when filters change
  useEffect(() => {
    fetchItems();
  }, [selectedCategory, searchQuery, fetchItems]);
  
  // Save item
  const handleSaveItem = async () => {
    if (!itemForm.name) {
      toast.error('Item name is required');
      return;
    }
    
    setLoading(true);
    try {
      if (editingItem) {
        await api.put(`/admin/finance/inventory/items/${editingItem.id}`, itemForm);
      } else {
        await api.post('/admin/finance/inventory/items', itemForm);
      }
      
      toast.success(editingItem ? 'Item updated' : 'Item created');
      setShowItemDialog(false);
      setEditingItem(null);
      resetItemForm();
      fetchItems();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };
  
  // Adjust inventory
  const handleAdjustInventory = async () => {
    if (!selectedItem || adjustForm.quantity <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/admin/finance/inventory/items/${selectedItem.id}/adjust`, {
        ...adjustForm,
        unit_cost: adjustForm.unit_cost ? parseFloat(adjustForm.unit_cost) : null
      });
      
      toast.success('Inventory adjusted');
      setShowAdjustDialog(false);
      setSelectedItem(null);
      setAdjustForm({ quantity: 0, type: 'add', reason: 'purchase', notes: '', unit_cost: '' });
      fetchItems();
      fetchLowStock();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust inventory');
    } finally {
      setLoading(false);
    }
  };
  
  // Log waste
  const handleLogWaste = async () => {
    if (!selectedItem || wasteForm.quantity <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/finance/inventory/waste', {
        inventory_item_id: selectedItem.id,
        ...wasteForm
      });
      
      toast.success('Waste logged');
      setShowWasteDialog(false);
      setSelectedItem(null);
      setWasteForm({ quantity: 0, reason: 'expired', notes: '' });
      fetchItems();
      fetchWasteLog();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log waste');
    } finally {
      setLoading(false);
    }
  };
  
  // Perform stock count
  const handleStockCount = async () => {
    const countsToSubmit = stockCounts.filter(c => c.counted_qty !== null);
    if (countsToSubmit.length === 0) {
      toast.error('Enter at least one count');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/admin/finance/inventory/stock-count', {
        counts: countsToSubmit.map(c => ({
          inventory_item_id: c.id,
          counted_qty: c.counted_qty
        })),
        notes: 'Manual stock count'
      });
      
      toast.success(`Stock count completed: ${res.data.summary.items_with_variance} items with variance`);
      setShowCountDialog(false);
      setStockCounts([]);
      fetchItems();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit stock count');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete item
  const handleDeleteItem = async (id) => {
    if (!confirm('Deactivate this inventory item?')) return;
    
    try {
      await api.delete(`/admin/finance/inventory/items/${id}`);
      toast.success('Item deactivated');
      fetchItems();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };
  
  // Reset forms
  const resetItemForm = () => {
    setItemForm({
      name: '', sku: '', category: 'raw_ingredients', subcategory: '', description: '',
      unit_of_measure: 'units', quantity_on_hand: 0, reorder_point: 10, reorder_quantity: 50,
      unit_cost: 0, storage_location: '', expiry_tracking: false, notes: ''
    });
  };
  
  // Edit item
  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || '',
      sku: item.sku || '',
      category: item.category || 'raw_ingredients',
      subcategory: item.subcategory || '',
      description: item.description || '',
      unit_of_measure: item.unit_of_measure || 'units',
      quantity_on_hand: item.quantity_on_hand || 0,
      reorder_point: item.reorder_point || 10,
      reorder_quantity: item.reorder_quantity || 50,
      unit_cost: item.unit_cost || 0,
      storage_location: item.storage_location || '',
      expiry_tracking: item.expiry_tracking || false,
      notes: item.notes || ''
    });
    setShowItemDialog(true);
  };
  
  // Open stock count dialog
  const openStockCount = () => {
    setStockCounts(items.map(i => ({
      id: i.id,
      name: i.name,
      sku: i.sku,
      expected: i.quantity_on_hand,
      counted_qty: null,
      unit: i.unit_of_measure
    })));
    setShowCountDialog(true);
  };
  
  // Update stock count
  const updateStockCount = (id, qty) => {
    setStockCounts(stockCounts.map(c => 
      c.id === id ? { ...c, counted_qty: qty === '' ? null : parseFloat(qty) } : c
    ));
  };
  
  // Get subcategories for selected category
  const getSubcategories = () => {
    const cat = categories[itemForm.category];
    return cat?.subcategories || [];
  };
  
  return (
    <div className="space-y-6" data-testid="detailed-inventory-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Detailed Inventory</h2>
          <p className="text-muted-foreground">
            Track stock levels, costs, and waste
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchItems(); fetchAnalytics(); fetchLowStock(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="secondary" onClick={openStockCount}>
            <FileText className="w-4 h-4 mr-2" />
            Stock Count
          </Button>
          <Button onClick={() => { resetItemForm(); setEditingItem(null); setShowItemDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Warehouse className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(analytics.total_inventory_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-xl font-bold text-foreground">{analytics.total_items}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${analytics.low_stock_items > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                  <AlertTriangle className={`w-5 h-5 ${analytics.low_stock_items > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock</p>
                  <p className={`text-xl font-bold ${analytics.low_stock_items > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {analytics.low_stock_items}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Waste Cost</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(analytics.total_waste_cost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inventory">
            <Package className="w-4 h-4 mr-2" />
            All Items
          </TabsTrigger>
          <TabsTrigger value="lowstock">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Low Stock ({lowStockItems.length})
          </TabsTrigger>
          <TabsTrigger value="waste">
            <TrendingDown className="w-4 h-4 mr-2" />
            Waste Log
          </TabsTrigger>
        </TabsList>
        
        {/* All Items Tab */}
        <TabsContent value="inventory" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categories).map(([id, cat]) => (
                  <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Items List */}
          {items.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No inventory items found</p>
                <Button className="mt-4" onClick={() => { resetItemForm(); setShowItemDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Item
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {items.map(item => {
                const isLowStock = item.quantity_available <= item.reorder_point;
                
                return (
                  <Card key={item.id} className={isLowStock ? 'border-amber-300 dark:border-amber-700' : ''}>
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isLowStock ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-primary/10'}`}>
                            <Package className={`w-5 h-5 ${isLowStock ? 'text-amber-600' : 'text-primary'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{item.name}</p>
                              {isLowStock && <Badge variant="outline" className="text-amber-600 border-amber-300">Low</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {item.sku} • {categories[item.category]?.name || item.category}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right hidden sm:block">
                            <p className="font-medium text-foreground">{item.quantity_on_hand} {item.unit_of_measure}</p>
                            <p className="text-sm text-muted-foreground">{formatCurrency(item.total_value)}</p>
                          </div>
                          {expandedItem === item.id ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {expandedItem === item.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="px-4 pb-4 border-t pt-4 space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">On Hand</p>
                                <p className="font-medium">{item.quantity_on_hand} {item.unit_of_measure}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Reserved</p>
                                <p className="font-medium">{item.quantity_reserved} {item.unit_of_measure}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Unit Cost</p>
                                <p className="font-medium">{formatCurrency(item.unit_cost)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Reorder Point</p>
                                <p className="font-medium">{item.reorder_point} {item.unit_of_measure}</p>
                              </div>
                            </div>
                            
                            {item.storage_location && (
                              <p className="text-sm text-muted-foreground">
                                Location: {item.storage_location}
                              </p>
                            )}
                            
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowAdjustDialog(true); }}
                              >
                                <ArrowUp className="w-4 h-4 mr-1" />
                                Adjust
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowWasteDialog(true); }}
                              >
                                <TrendingDown className="w-4 h-4 mr-1" />
                                Log Waste
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleEditItem(item); }}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600"
                                onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        {/* Low Stock Tab */}
        <TabsContent value="lowstock" className="space-y-4">
          {lowStockItems.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Check className="w-12 h-12 mx-auto mb-3 text-green-600" />
                <p className="text-green-600 font-medium">All items are well stocked!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {lowStockItems.map(item => (
                <Card key={item.id} className="border-amber-300 dark:border-amber-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{item.name}</p>
                          <p className="text-sm text-muted-foreground">{item.sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-600 font-medium">{item.quantity_available} {item.unit_of_measure}</p>
                        <p className="text-sm text-muted-foreground">
                          Reorder: {item.suggested_order_qty} {item.unit_of_measure}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Waste Log Tab */}
        <TabsContent value="waste" className="space-y-4">
          {wasteLog.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <TrendingDown className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No waste recorded yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {wasteLog.map(entry => (
                <Card key={entry.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">{entry.item_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {entry.quantity} {entry.unit_of_measure} - {WASTE_REASONS.find(r => r.id === entry.reason)?.name || entry.reason}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-red-600 font-medium">-{formatCurrency(entry.total_cost)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Add/Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={(open) => {
        setShowItemDialog(open);
        if (!open) { setEditingItem(null); resetItemForm(); }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
            <DialogDescription>Enter item details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item Name *</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="Coffee Beans"
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={itemForm.sku}
                  onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                  placeholder="Auto-generated"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v, subcategory: '' })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([id, cat]) => (
                      <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory</Label>
                <Select value={itemForm.subcategory} onValueChange={(v) => setItemForm({ ...itemForm, subcategory: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getSubcategories().map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit of Measure</Label>
                <Select value={itemForm.unit_of_measure} onValueChange={(v) => setItemForm({ ...itemForm, unit_of_measure: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.unit_cost}
                  onChange={(e) => setItemForm({ ...itemForm, unit_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            {!editingItem && (
              <div>
                <Label>Initial Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={itemForm.quantity_on_hand}
                  onChange={(e) => setItemForm({ ...itemForm, quantity_on_hand: parseFloat(e.target.value) || 0 })}
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reorder Point</Label>
                <Input
                  type="number"
                  min="0"
                  value={itemForm.reorder_point}
                  onChange={(e) => setItemForm({ ...itemForm, reorder_point: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Reorder Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={itemForm.reorder_quantity}
                  onChange={(e) => setItemForm({ ...itemForm, reorder_quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div>
              <Label>Storage Location</Label>
              <Input
                value={itemForm.storage_location}
                onChange={(e) => setItemForm({ ...itemForm, storage_location: e.target.value })}
                placeholder="Walk-in cooler, Shelf A3..."
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={itemForm.notes}
                onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingItem ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Adjust Inventory Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
            <DialogDescription>
              {selectedItem?.name} - Current: {selectedItem?.quantity_on_hand} {selectedItem?.unit_of_measure}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm({ ...adjustForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add Stock</SelectItem>
                    <SelectItem value="remove">Remove Stock</SelectItem>
                    <SelectItem value="set">Set Exact Quantity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div>
              <Label>Reason</Label>
              <Select value={adjustForm.reason} onValueChange={(v) => setAdjustForm({ ...adjustForm, reason: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_REASONS.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {adjustForm.reason === 'purchase' && (
              <div>
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adjustForm.unit_cost}
                  onChange={(e) => setAdjustForm({ ...adjustForm, unit_cost: e.target.value })}
                  placeholder="Update unit cost if changed"
                />
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={adjustForm.notes}
                onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancel</Button>
            <Button onClick={handleAdjustInventory} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Log Waste Dialog */}
      <Dialog open={showWasteDialog} onOpenChange={setShowWasteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Waste</DialogTitle>
            <DialogDescription>
              {selectedItem?.name} - Available: {selectedItem?.quantity_on_hand} {selectedItem?.unit_of_measure}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={wasteForm.quantity}
                  onChange={(e) => setWasteForm({ ...wasteForm, quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Select value={wasteForm.reason} onValueChange={(v) => setWasteForm({ ...wasteForm, reason: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WASTE_REASONS.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={wasteForm.notes}
                onChange={(e) => setWasteForm({ ...wasteForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            
            {wasteForm.quantity > 0 && selectedItem && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                <p className="text-red-600 font-medium">
                  Cost: {formatCurrency(wasteForm.quantity * (selectedItem.unit_cost || 0))}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWasteDialog(false)}>Cancel</Button>
            <Button onClick={handleLogWaste} disabled={loading} variant="destructive">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingDown className="w-4 h-4 mr-2" />}
              Log Waste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Stock Count Dialog */}
      <Dialog open={showCountDialog} onOpenChange={setShowCountDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Count</DialogTitle>
            <DialogDescription>
              Enter counted quantities for each item
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4 max-h-[400px] overflow-y-auto">
            {stockCounts.map(item => (
              <div key={item.id} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{item.name}</p>
                  <p className="text-sm text-muted-foreground">{item.sku}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Expected: {item.expected}</p>
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Count"
                    value={item.counted_qty ?? ''}
                    onChange={(e) => updateStockCount(item.id, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCountDialog(false)}>Cancel</Button>
            <Button onClick={handleStockCount} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Submit Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
